import { ConnectionQualityLevels } from "@minervaproject/avsubsystem-core";
import { isArray, round, uniq } from "lodash-es";

import { RootState } from "..";
import { PubsubVirtualUserState } from "../../shared/types/av-state";
import { MonitoringEventTypeType, UserStateStatsType } from "../../shared/types/monitoring";

import { createAppSelector, selectCurrentSectionId } from "./base";

import { filterMap } from "utils/collections";
import { getStatsByEventType } from "utils/monitoring";

export const selectMonitoringEventCountBySectionIdAndUserIdAndType = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, sectionId: string | null) => sectionId,
    (state: RootState, sectionId: string | null, userId: string) => userId,
    (
      state: RootState,
      sectionId: string | null,
      userId: string,
      eventType: MonitoringEventTypeType,
    ) => eventType,
  ],
  (stats, sectionId, userId, eventType) => {
    if (!sectionId || !stats[sectionId] || !stats[sectionId][eventType]) {
      return 0;
    }
    const data = stats[sectionId][eventType];
    if (!isArray(data.userIds)) {
      return 0;
    }
    return data.userIds.filter((id) => id === userId).length;
  },
);

export const selectMonitoringEventCountBySectionIdAndTargetClassUserIdAndType = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, sectionId: string | null) => sectionId,
    (state: RootState, sectionId: string | null, targetClassUserId: string | null) =>
      targetClassUserId,
    (
      state: RootState,
      sectionId: string | null,
      targetClassUserId: string | null,
      eventType: MonitoringEventTypeType,
    ) => eventType,
  ],
  (stats, sectionId, targetClassUserId, eventType) => {
    if (!sectionId || !targetClassUserId || !stats[sectionId] || !stats[sectionId][eventType]) {
      return 0;
    }
    const data = stats[sectionId][eventType];
    if (!isArray(data.targetClassUserIds)) {
      return 0;
    }
    return data.targetClassUserIds.filter((id) => id === targetClassUserId).length;
  },
);

export const selectUserCountWithMonitoringEventsForCurrentSection = createAppSelector(
  [
    (state: RootState) => {
      const sectionId = selectCurrentSectionId(state);
      return sectionId ? state.monitoring.stats[sectionId] : undefined;
    },
  ],
  (sectionStats) => {
    if (!sectionStats) {
      return 0;
    }
    return uniq(Object.values(sectionStats).flatMap((eventStats) => eventStats.userIds)).length;
  },
);

const defaultUserStateStats: UserStateStatsType = {
  affectedUsers: [],
  percentAffectedUsers: null,
  formattedPercentage: "-",
};

const computeUserStateStats = (
  userData: Record<string, PubsubVirtualUserState>,
  nParticipants: number,
  filterFn: (userState: PubsubVirtualUserState) => boolean,
): UserStateStatsType => {
  if (!userData || !nParticipants) {
    return defaultUserStateStats;
  }
  const affectedUsers = filterMap(
    Object.entries(userData),
    ([, userState]) => filterFn(userState),
    ([classUserId]) => classUserId,
  );
  const percentAffectedUsers = (affectedUsers.length / nParticipants) * 100;
  return {
    affectedUsers: uniq(affectedUsers),
    percentAffectedUsers,
    formattedPercentage: `${round(percentAffectedUsers, 2)}%`,
  };
};

const selectPubsubVirtualUsers = (state: RootState) => state.pubsub_virtual_class_state.users;

export const selectPubsubConnectionStatsForCurrentSection = createAppSelector(
  [selectPubsubVirtualUsers, selectUserCountWithMonitoringEventsForCurrentSection],
  (userData, nParticipants) =>
    computeUserStateStats(
      userData,
      nParticipants,
      (u) => u.pubsubWebsocketStatus === "disconnected" || u.pubsubWebsocketStatus === "failed",
    ),
);

export const selectMicErrorStatsForCurrentSection = createAppSelector(
  [selectPubsubVirtualUsers, selectUserCountWithMonitoringEventsForCurrentSection],
  (userData, nParticipants) => computeUserStateStats(userData, nParticipants, (u) => !!u.micError),
);

export const selectCameraErrorStatsForCurrentSection = createAppSelector(
  [selectPubsubVirtualUsers, selectUserCountWithMonitoringEventsForCurrentSection],
  (userData, nParticipants) =>
    computeUserStateStats(userData, nParticipants, (u) => !!u.cameraError),
);

export const selectHighPingStatsForCurrentSection = createAppSelector(
  [selectPubsubVirtualUsers, selectUserCountWithMonitoringEventsForCurrentSection],
  (userData, nParticipants) =>
    computeUserStateStats(userData, nParticipants, (u) => !!u.pubsubPingRtt?.isHigh),
);

export const selectPoorConnectionStatsForCurrentSection = createAppSelector(
  [selectPubsubVirtualUsers, selectUserCountWithMonitoringEventsForCurrentSection],
  (userData, nParticipants) =>
    computeUserStateStats(
      userData,
      nParticipants,
      (u) => u.connectionQuality === ConnectionQualityLevels.POOR,
    ),
);

export const selectMonitoringEventStatsBySectionIdAndType = createAppSelector(
  [
    (state: RootState, sectionId: string) => state.monitoring.stats[sectionId],
    (_state: RootState, _sectionId: string, eventType: MonitoringEventTypeType) => eventType,
    (
      _state: RootState,
      _sectionId: string,
      _eventType: MonitoringEventTypeType,
      useTargetClassUserIds?: boolean,
    ) => !!useTargetClassUserIds,
  ],
  (sectionStats, eventType, useTargetClassUserIds) =>
    getStatsByEventType(sectionStats, eventType, useTargetClassUserIds),
);
